import gsap from 'gsap';

import { BaseComponentDrawer } from '../../../core/abstraction/BaseComponentDrawer';

import { crystalTypes, oddCrystalsResourceNames, oddCrystalsResources } from '../../../core/gameConstants';
import { ACTIVE_ODD, GAME_MODE, ODD_CRYSTAL_ACTIVE_STATE } from '../../../stateManagment/gameState';
import { gamePadDrawer } from '../../gamePad/GamePadDrawer';
import { soundService } from '../../../services/helperService/PixiSoundService';
import { gameMobileConfig } from '../../../core/configs';
import { GAME_MODES } from '../../../core/constats';

function onStartEvent() {
  if (ACTIVE_ODD.getValue()) {
    this.kill();
    return true;
  }
  return false;
}

export class BaseOddCrystalsDrawer extends BaseComponentDrawer {
  constructor() {
    super();
    this.loader = this.createLoader();
    this.resources = null;
    this.crystals = null;
    this.odds = [
      {
        name: oddCrystalsResourceNames.odd2,
        crystal: crystalTypes.crystal_blue,
      },
      {
        name: oddCrystalsResourceNames.odd3,
        crystal: crystalTypes.crystal_magenta,
      },
      {
        name: oddCrystalsResourceNames.odd5,
        crystal: crystalTypes.crystal_green,
      },
      {
        name: oddCrystalsResourceNames.odd60,
        crystal: crystalTypes.crystal_yellow,
      },
    ];
    this.oddAnimationStop = false;
  }

  async loadResource() {
    if (!this.resources) {
      const { res } = await super.loadResource(oddCrystalsResources, this.loader);
      this.resources = {
        ...this.resources,
        ...res,
      };
    }
    return this.resources;
  }

  getOddCrystal({ name, size, onClick, crystal: type, scale = 1 } = {}) {
    const rect = new this.PIXI.Graphics()
      .drawRect(
        0,
        0,
        size,
        size,
      )
      .endFill();
    const crystal = this.createSprite(this.resources[name]);
    crystal.anchor.set(.5);
    crystal.scale.set(scale);
    crystal.interactive = true;
    crystal.buttonMode = true;
    crystal.on('pointerdown', (event) => {
      event.stopPropagation();
      gamePadDrawer.resetOpenedState();
      return onClick({ crystal: type, target: crystal });
    });
    this.setPosition(crystal, { x: size / 2, y: size / 2 });
    rect.addChild(crystal);
    this.crystals = {
      ...this.crystals,
      [name]: crystal,
    };
    return rect;
  }

  setupOddUnSelectedAnimation({ scale = 1.2, duration = .5 } = {}) {
    if (this.oddAnimationStop) {
      return;
    }
    if (ACTIVE_ODD.getValue() === null) {
      if (this.crystals) {
        this.odds.forEach(({ crystal: type, name }, index, { length }) => {
          const crystal = this.crystals[name];
          gsap.to(
            crystal,
            {
              delay: index * duration,
              duration,
              pixi: { scale: scale },
              onStart: function () {
                if (onStartEvent.call(this) && type !== ACTIVE_ODD.getValue()) {
                  gsap.to(crystal, { duration, pixi: { scale: 1 } });
                }
              },
              onComplete: () => {
                if (index === length - 1) {
                  this.setupOddUnSelectedAnimation();
                }
              },
            },
          );
          gsap.to(
            crystal,
            {
              delay: (index + 1) * duration,
              duration,
              pixi: { scale: 1 },
              onStart: function () {
                onStartEvent.call(this);
                if (onStartEvent.call(this) && type !== ACTIVE_ODD.getValue()) {
                  gsap.to(crystal, { duration, pixi: { scale: 1 } });
                }
              },
            },
          );
        });
      }
    } else {
      this.oddAnimationStop = true;
    }
  }

  setOddCrystalObserver({
    activeConfig = [1.2, 1],
    disabledConfig = [1, .5],
  } = {}) {
    this.addSubscriber(ACTIVE_ODD.subscribe((activeOdd) => {
      if (!this.crystals) {
        return;
      }
      this.odds.forEach(({ crystal, name }) => {
        const [scale, alpha] = activeOdd === crystal ? activeConfig : disabledConfig;
        if (this.crystals[name].scale !== scale) {
          gsap.to(this.crystals[name], {
            duration: .3,
            pixi: {
              scale,
            },
            alpha,
          });
        }
      });
    }, true));
  }

  getOddCrystals({
    parentWidth = gameMobileConfig.config.width,
  } = {}) {
    const activeConfig = GAME_MODE.getValue() === GAME_MODES.MOBILE ? [1.2, 1] : [1.3, 1];
    const disabledConfig = GAME_MODE.getValue() === GAME_MODES.MOBILE ? [1, .5] : [1.1, .5];
    let space = null;
    const count = this.odds.length;
    const width = this.config.size;
    return this.odds.map(({ name, crystal }, index) => {
      const odd = this.getOddCrystal({
        name,
        crystal,
        size: this.config.size,
        scale: this.config.scale,
        onClick: ({ crystal }) => {
          if (ODD_CRYSTAL_ACTIVE_STATE.getValue()) {
            soundService.playOddCrystalClick();
            ACTIVE_ODD.next(crystal);
          }
        },
      });
      const c = this.crystals[name];

      this.registerPointerover(c, () => {
        if (ACTIVE_ODD.getValue() !== crystal) {
          gsap.to(c, {
            duration: .2,
            pixi: {
              scale: activeConfig[0],
            },
            alpha: .7,
          });
        }
      });

      this.registerPointerout(c, () => {
        if (ACTIVE_ODD.getValue() !== crystal) {
          gsap.to(c, {
            duration: .2,
            pixi: {
              scale: disabledConfig[0],
            },
            alpha: disabledConfig[1],
          });
        }
      });

      if (space === null) {
        space = (parentWidth - ((count - 1) * this.config.margin + count * width)) / 2;
      }
      const x = space + index * (this.config.margin + width);
      this.setPosition(odd, { x, y: 0 });
      return odd;
    });
  }
}
